import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import { type IWhTaskFilter, useWhTaskFiltersStore } from 'shared/stores/use-wh-task-filters';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { ClientFormSelect } from 'components/common/client-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { UserFormSelect } from 'components/common/user-form-select';
import { WarehouseFormSelect } from 'components/common/warehouse-form-select';
import { whTaskTypeOptions, whTaskStatusOptions } from './constants';
import type { IWhTaskFiltersDrawerProps } from './types';
import styles from './wh-task-filters-drawer.module.scss';

export const WhTaskFiltersDrawer: React.FC<IWhTaskFiltersDrawerProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useWhTaskFiltersStore();

  const handleSuccess = debounce(() => transformAndSetFilters<IWhTaskFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='wh-task-filter' layout='horizontal' initialValues={filters}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='type' className={styles.input}>
            <Select
              placeholder={t('common:type')}
              disabled={!!search}
              options={whTaskTypeOptions}
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>
          <Form.Item name='statuses' className={styles.input}>
            <Select
              placeholder='Statuses'
              mode='multiple'
              disabled={!!search}
              options={whTaskStatusOptions}
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>
          <WarehouseFormSelect
            name='warehouseId'
            placeholder={t('common:warehouse')}
            className={styles.input}
            disabled={!!search}
            handleChange={handleSuccess}
          />
          <ClientFormSelect
            name='clientId'
            placeholder={t('client:client')}
            handleClientChange={handleSuccess}
            className={styles.input}
            allowClear
          />
          <UserFormSelect
            name='userId'
            placeholder={t('common:manager')}
            handleUserChange={handleSuccess}
            className={styles.input}
            disabled={!!search}
            allowClear
          />
          <DataRangeFormPicker
            label='Arrival dates range'
            name='arrivalDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.dateRangePicker}
          />
          <DataRangeFormPicker
            label='Departure dates range'
            name='departureDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.dateRangePicker}
          />
        </Space>
      </Form>
    </Drawer>
  );
};
